import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi, tokenStore } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import JanteLogo from "@/components/JanteLogo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await publicApi.post("/auth/login", { email, password });
      tokenStore.set(res.data.token);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Login failed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6 py-16"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#C03028]/30">
            <Lock size={12} className="text-[#C03028]" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C03028]">
              Studio access
            </span>
          </div>
          <h1 className="font-serif-display mt-6 text-4xl text-[#2A1F1A]">
            Sasha<span className="text-[#B0895C]">.</span>host
          </h1>
          <p className="mt-3 text-sm text-[#6E6E70]">
            Sign in to the studio dashboard.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-[#E3DED5] p-8 space-y-6"
          data-testid="admin-login-form"
        >
          <div>
            <Label className="eyebrow">Email</Label>
            <Input
              data-testid="admin-login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@sasha.host"
              className="editorial-input mt-2"
            />
          </div>

          <div>
            <Label className="eyebrow">Password</Label>
            <Input
              data-testid="admin-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="editorial-input mt-2"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary w-full py-3 text-sm tracking-wide rounded-none inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-[#6E6E70]/70">
          Access is reserved for the Sasha studio team.
        </p>
      </motion.div>
    </main>
  );
}
